import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ChevronDown, Mail, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import LanguageSwitcher from '../components/LanguageSwitcher';

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-[#E5E3DB]/80 last:border-b-0">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex items-center justify-between py-5 px-1 text-left group"
        data-testid={`faq-q-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className={`text-base font-medium pr-8 transition-colors ${open ? 'text-[#6E1A24]' : 'text-[#2D2D2A] group-hover:text-[#6E1A24]'}`}>
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 flex-shrink-0 text-[#6C6C67] transition-transform duration-200 ${open ? 'rotate-180 text-[#6E1A24]' : ''}`} />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-out ${open ? 'max-h-96 pb-5' : 'max-h-0'}`}
      >
        <p className="text-[#6C6C67] leading-relaxed px-1">{answer}</p>
      </div>
    </div>
  );
}

function FAQSection({ title, items }) {
  return (
    <div className="mb-12">
      <h2 className="text-xl font-serif font-medium text-[#2D2D2A] mb-4 pl-1">{title}</h2>
      <div className="bg-white rounded-2xl border border-[#E5E3DB] px-6">
        {items.map((item) => (
          <FAQItem key={item.q} question={item.q} answer={item.a} />
        ))}
      </div>
    </div>
  );
}

export default function FAQ() {
  const { t } = useTranslation();

  const sections = [
    {
      title: t('faq.cat_general'),
      items: [
        { q: t('faq.q_what_is'), a: t('faq.a_what_is') },
        { q: t('faq.q_who_for'), a: t('faq.a_who_for') },
        { q: t('faq.q_only_portuguese'), a: t('faq.a_only_portuguese') },
      ]
    },
    {
      title: t('faq.cat_account'),
      items: [
        { q: t('faq.q_create_account'), a: t('faq.a_create_account') },
        { q: t('faq.q_why_photo'), a: t('faq.a_why_photo') },
        { q: t('faq.q_edit_profile'), a: t('faq.a_edit_profile') },
        { q: t('faq.q_delete_account'), a: t('faq.a_delete_account') },
      ]
    },
    {
      title: t('faq.cat_matching'),
      items: [
        { q: t('faq.q_how_matching'), a: t('faq.a_how_matching') },
        { q: t('faq.q_message_after_like'), a: t('faq.a_message_after_like') },
        { q: t('faq.q_who_liked'), a: t('faq.a_who_liked') },
      ]
    },
    {
      title: t('faq.cat_premium'),
      items: [
        { q: t('faq.q_plan_difference'), a: t('faq.a_plan_difference') },
        { q: t('faq.q_how_upgrade'), a: t('faq.a_how_upgrade') },
        { q: t('faq.q_auto_charge'), a: t('faq.a_auto_charge') },
      ]
    },
    {
      title: t('faq.cat_safety'),
      items: [
        { q: t('faq.q_verified'), a: t('faq.a_verified') },
        { q: t('faq.q_data_protection'), a: t('faq.a_data_protection') },
        { q: t('faq.q_report_block'), a: t('faq.a_report_block') },
      ]
    },
    {
      title: t('faq.cat_travel'),
      items: [
        { q: t('faq.q_travel_mode'), a: t('faq.a_travel_mode') },
        { q: t('faq.q_use_europe'), a: t('faq.a_use_europe') },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEOHead title="FAQ" description="Frequently asked questions about LusoLove, the dating platform for Portuguese people in Europe. Learn about our features, safety, and how to get started." path="/faq" />
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#FAF9F6]/90 border-b border-[#E5E3DB]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="faq-logo">
            <Heart className="w-9 h-9 text-[#6E1A24] group-hover:scale-110 transition-transform" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
            <span className="text-2xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/login">
              <Button className="bg-[#6E1A24] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#8A2331] transition-all" data-testid="faq-signin">
                {t('landing.sign_in')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Title */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#6E1A24]/10 mb-6">
              <HelpCircle className="w-8 h-8 text-[#6E1A24]" strokeWidth={1.5} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-serif font-light text-[#2D2D2A] mb-4" data-testid="faq-title">
              {t('faq.title')}
            </h1>
            <p className="text-lg text-[#6C6C67] font-light max-w-lg mx-auto">
              {t('faq.subtitle')}
            </p>
          </div>

          {/* FAQ Sections */}
          {sections.map((section) => (
            <FAQSection key={section.title} title={section.title} items={section.items} />
          ))}

          {/* Support CTA */}
          <div className="bg-[#6E1A24] rounded-3xl p-10 sm:p-12 text-center mt-4" data-testid="faq-support-cta">
            <h3 className="text-2xl font-serif font-light text-white mb-3">{t('faq.still_need_help')}</h3>
            <p className="text-white/70 mb-8 max-w-md mx-auto">{t('faq.support_desc')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button className="bg-white text-[#6E1A24] rounded-full px-8 py-3 font-semibold hover:bg-[#FAF9F6] transition-all" data-testid="faq-contact-btn">
                  {t('faq.contact_us')}
                </Button>
              </Link>
              <a href="mailto:contact@lusolove.com" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                <span className="font-medium">contact@lusolove.com</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 bg-[#2D2D2A] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-[#C97A63]" strokeWidth={1.5} fill="currentColor" fillOpacity={0.3} />
            <span className="text-xl font-serif">LusoLove</span>
          </div>
          <div className="flex gap-8 text-sm text-white/60">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/faq" className="hover:text-white transition-colors">{t('nav.faq', 'FAQ')}</Link>
            <Link to="/contact" className="hover:text-white transition-colors">{t('nav.contact', 'Contact')}</Link>
          </div>
          <p className="text-sm text-white/40">{t('landing.footer_tagline')}</p>
        </div>
      </footer>
    </div>
  );
}
